/**
 * Living-sky canvas engine for the marketing atmosphere.
 *
 * The sky is a TALL virtual starscape sized to the page's scroll range and panned by scroll, so
 * scrolling feels like panning down a real sky — named constellations reveal from below as you go.
 * Off-screen stars/figures are culled each frame (only a viewport-worth is drawn); warm ember motes
 * rise from the viewport base (the hearth stays anchored); an occasional shooting star crosses the
 * upper sky. Scroll parallax for the CSS gradient layers is published as CSS vars on `.eb-bg`.
 *
 * The virtual sky is sized from the measured page height (re-measured via a ResizeObserver as late
 * content settles) so it never freezes before the page bottom, and constellations are placed across
 * the achievable pan range so they all reveal. Under reduced motion there is no self-running loop,
 * but a passive scroll listener still pans the (static) sky so lower figures + the parallax layers
 * respond to the user's own scrolling.
 *
 * marketing-site is a separate Vite build with its own palette — color literals here are intentional.
 */

const PAN_FACTOR = 0.42; // sky pans this fraction of scroll distance (parallax feel)
const SEED = 730214;

type Tier = 'dust' | 'normal' | 'anchor';
type Twinkle = { amp: number; phase: number; speed: number };
type Star = { x: number; y: number; r: number; base: number; warm: boolean; tw?: Twinkle };
type Ember = {
  x: number;
  y: number;
  r: number;
  vy: number;
  drift: number;
  phase: number;
  sp: number;
  pop: boolean;
};
type Shooting = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  len: number;
};

/** A constellation. cx is centre x (0..1 of width); cy is a spread fraction (0..1) mapped to a
 *  position within the achievable pan range at draw time; size scales it; node offsets are ~ -1..1. */
// Each node is [ox, oy, mag]: unit offset (~ -1..1) plus a relative magnitude (0..1, 1 = brightest),
// which scales the star's size + glow so figures read like real charts, not evenly-dotted scribbles.
type CNode = readonly [number, number, number];
type Figure = {
  cx: number;
  cy: number;
  size: number;
  nodes: readonly CNode[];
  links: readonly (readonly [number, number])[];
  travel: boolean;
  lineBase: number;
};

// Real constellations with true relative star geometry, spread top → bottom (by cy) so panning
// reveals them in turn. Only the crown (Corona Borealis, the "ward") carries the travelling light.
const CONSTELLATIONS: readonly Figure[] = [
  {
    // Corona Borealis — the "ward". A graceful seven-star arc with Alphecca (α) brightest at its
    // crest. Sits just below the hero fold and carries the travelling light.
    cx: 0.82,
    cy: 0.24,
    size: 0.13,
    travel: true,
    lineBase: 0.42,
    nodes: [
      [-1, 0.34, 0.42],
      [-0.66, 0.02, 0.48],
      [-0.29, -0.2, 0.52],
      [0.04, -0.28, 1],
      [0.39, -0.2, 0.54],
      [0.72, 0.02, 0.48],
      [1, 0.34, 0.42],
    ],
    links: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [4, 5],
      [5, 6],
    ],
  },
  {
    // Cassiopeia — the W (Caph, Schedar, Gamma, Ruchbah, Segin).
    cx: 0.2,
    cy: 0.33,
    size: 0.09,
    travel: false,
    lineBase: 0.28,
    nodes: [
      [-1, -0.28, 0.58],
      [-0.5, 0.42, 0.66],
      [0, -0.16, 0.74],
      [0.5, 0.46, 0.62],
      [1, -0.06, 0.54],
    ],
    links: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
    ],
  },
  {
    // Boötes — the Kite, Arcturus (α) blazing at the base point.
    cx: 0.18,
    cy: 0.25,
    size: 0.1,
    travel: false,
    lineBase: 0.26,
    nodes: [
      [0.02, 1, 1],
      [-0.46, 0.24, 0.5],
      [-0.34, -0.52, 0.48],
      [0.04, -1, 0.6],
      [0.52, -0.5, 0.52],
      [0.42, 0.12, 0.55],
    ],
    links: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [4, 5],
      [5, 0],
    ],
  },
  {
    // Lyra — Vega (α) over the little rhombus (Sheliak, Sulafat, ζ, δ).
    cx: 0.63,
    cy: 0.37,
    size: 0.075,
    travel: false,
    lineBase: 0.24,
    nodes: [
      [0, -1, 1],
      [-0.4, 0.02, 0.45],
      [0.32, -0.08, 0.45],
      [0.5, 0.68, 0.4],
      [-0.18, 0.8, 0.4],
    ],
    links: [
      [0, 1],
      [0, 2],
      [1, 4],
      [4, 3],
      [3, 2],
    ],
  },
  {
    // Orion — shoulders (Betelgeuse, Bellatrix), belt (Alnitak/Alnilam/Mintaka), feet (Saiph, Rigel).
    cx: 0.52,
    cy: 0.5,
    size: 0.12,
    travel: false,
    lineBase: 0.28,
    nodes: [
      [-0.5, -0.9, 0.95],
      [0.5, -0.78, 0.68],
      [0.2, 0.06, 0.78],
      [0, 0.13, 0.78],
      [-0.2, 0.2, 0.74],
      [-0.52, 0.98, 0.66],
      [0.62, 0.92, 1],
    ],
    links: [
      [0, 1],
      [0, 4],
      [1, 2],
      [4, 3],
      [3, 2],
      [4, 5],
      [2, 6],
    ],
  },
  {
    // Big Dipper (Ursa Major) — bowl (Dubhe, Merak, Phecda, Megrez) + handle (Alioth, Mizar, Alkaid).
    cx: 0.28,
    cy: 0.62,
    size: 0.13,
    travel: false,
    lineBase: 0.26,
    nodes: [
      [-1, -0.4, 0.75],
      [-1, 0.16, 0.7],
      [-0.55, 0.32, 0.6],
      [-0.5, -0.2, 0.5],
      [-0.02, -0.32, 0.7],
      [0.5, -0.18, 0.72],
      [1, 0.14, 0.68],
    ],
    links: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
      [3, 4],
      [4, 5],
      [5, 6],
    ],
  },
  {
    // Cygnus — the Northern Cross (Deneb, Sadr, Albireo spine; Gienah, δ wings).
    cx: 0.76,
    cy: 0.74,
    size: 0.11,
    travel: false,
    lineBase: 0.26,
    nodes: [
      [0, -1, 0.9],
      [0.04, -0.08, 0.58],
      [0.08, 0.92, 0.54],
      [-0.9, -0.22, 0.54],
      [0.86, -0.34, 0.5],
    ],
    links: [
      [0, 1],
      [1, 2],
      [3, 1],
      [1, 4],
    ],
  },
  {
    // Leo — the Sickle (Regulus up through the mane) joined to the hindquarters triangle
    // (Chort, Denebola, Zosma).
    cx: 0.26,
    cy: 0.87,
    size: 0.12,
    travel: false,
    lineBase: 0.26,
    nodes: [
      [-0.6, 0.52, 1],
      [-0.7, 0.1, 0.44],
      [-0.54, -0.28, 0.6],
      [-0.32, -0.52, 0.5],
      [-0.06, -0.44, 0.46],
      [0.5, 0.16, 0.5],
      [1, 0.02, 0.68],
      [0.42, -0.24, 0.5],
    ],
    links: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [0, 5],
      [5, 6],
      [6, 7],
      [7, 5],
    ],
  },
];

const PLEIADES = {
  cx: 0.58,
  cy: 0.95,
  size: 0.045,
  points: [
    [-0.5, 0.2],
    [0.1, -0.5],
    [0.5, 0.1],
    [0.2, 0.6],
    [-0.3, -0.4],
    [-0.7, -0.2],
    [0.6, -0.4],
  ] as const,
};

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeStarSprite(warm: boolean): HTMLCanvasElement {
  const S = 64;
  const c = document.createElement('canvas');
  c.width = S;
  c.height = S;
  const g = c.getContext('2d');
  if (!g) return c;
  const m = S / 2;
  const grad = g.createRadialGradient(m, m, 0, m, m, m);
  if (warm) {
    grad.addColorStop(0, 'rgba(255,245,224,1)');
    grad.addColorStop(0.14, 'rgba(255,231,198,0.9)');
    grad.addColorStop(0.42, 'rgba(244,197,109,0.32)');
    grad.addColorStop(1, 'rgba(244,197,109,0)');
  } else {
    grad.addColorStop(0, 'rgba(241,247,255,1)');
    grad.addColorStop(0.16, 'rgba(226,236,252,0.85)');
    grad.addColorStop(0.46, 'rgba(190,210,240,0.28)');
    grad.addColorStop(1, 'rgba(190,210,240,0)');
  }
  g.fillStyle = grad;
  g.fillRect(0, 0, S, S);
  return c;
}

function makeEmberSprite(): HTMLCanvasElement {
  const S = 48;
  const c = document.createElement('canvas');
  c.width = S;
  c.height = S;
  const g = c.getContext('2d');
  if (!g) return c;
  const m = S / 2;
  const grad = g.createRadialGradient(m, m, 0, m, m, m);
  grad.addColorStop(0, 'rgba(255,196,140,1)');
  grad.addColorStop(0.4, 'rgba(255,150,90,0.5)');
  grad.addColorStop(1, 'rgba(255,150,90,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, S, S);
  return c;
}

export type LivingSkyHandle = { destroy: () => void };

export function createLivingSky(canvas: HTMLCanvasElement, reduced: boolean): LivingSkyHandle {
  const noop = { destroy: () => {} };
  const rawCtx = canvas.getContext('2d');
  if (!rawCtx) return noop;
  const ctx: CanvasRenderingContext2D = rawCtx;
  const parent = canvas.parentElement;

  const spWarm = makeStarSprite(true);
  const spCool = makeStarSprite(false);
  const spEmber = makeEmberSprite();

  let W = 0;
  let H = 0;
  let dpr = 1;
  let skyH = 0; // virtual sky height (device px), sized to the page
  let panRange = 0; // = skyH - H, the max pan
  let stars: Star[] = [];
  let embers: Ember[] = [];
  let shooting: Shooting | null = null;
  let nextShootAt = 0;
  let startTs = 0;
  let last = 0;
  let raf = 0;
  let resizeTimer = 0;
  let roTimer = 0;
  let scrollRaf = 0;

  function blitStar(warm: boolean, x: number, y: number, r: number, a: number) {
    if (a <= 0) return;
    const half = r * (warm ? 3.6 : 2.9);
    ctx.globalAlpha = a > 1 ? 1 : a;
    ctx.drawImage(warm ? spWarm : spCool, x - half, y - half, half * 2, half * 2);
    ctx.globalAlpha = 1;
  }
  function blitEmber(x: number, y: number, r: number, a: number) {
    ctx.globalAlpha = a > 1 ? 1 : a;
    const half = r * 3;
    ctx.drawImage(spEmber, x - half, y - half, half * 2, half * 2);
    ctx.globalAlpha = 1;
  }

  function pageMaxScrollPx(): number {
    return Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight) * dpr;
  }
  // Size the sky so the whole page pans it (min 1.2 extra viewports even on short pages).
  function computeSky(): number {
    const maxPan = pageMaxScrollPx() * PAN_FACTOR;
    panRange = Math.max(maxPan, H * 1.2);
    return H + panRange;
  }

  function build() {
    skyH = computeSky();
    const rnd = mulberry32(SEED);
    stars = [];
    embers = [];
    const span = Math.min(skyH / H, 6); // density scales with the sky, capped so tall pages stay bounded
    const nDust = Math.round(112 * span);
    const nNormal = Math.round(88 * span);
    const nAnchor = Math.round(17 * span);

    const clusters: { x: number; y: number; sx: number; sy: number }[] = [];
    const nClusters = Math.max(3, Math.round(2 * span));
    for (let c = 0; c < nClusters; c++) {
      clusters.push({
        x: rnd() * W,
        y: rnd() * skyH,
        sx: (0.05 + rnd() * 0.04) * W,
        sy: (0.04 + rnd() * 0.04) * H,
      });
    }

    const place = (tier: Tier): { x: number; y: number } => {
      if (rnd() < (tier === 'dust' ? 0.3 : 0.18)) {
        const cl = clusters[(rnd() * clusters.length) | 0];
        return { x: cl.x + (rnd() - 0.5) * 2 * cl.sx, y: cl.y + (rnd() - 0.5) * 2 * cl.sy };
      }
      return { x: rnd() * W, y: rnd() * skyH };
    };
    const mk = (tier: Tier): Star => {
      const p = place(tier);
      const warm = rnd() < (tier === 'dust' ? 0.08 : tier === 'anchor' ? 0.34 : 0.2);
      let r: number;
      let base: number;
      if (tier === 'dust') {
        r = (0.4 + rnd() * 0.6) * dpr;
        base = 0.2 + rnd() * 0.26;
      } else if (tier === 'normal') {
        r = (0.6 + rnd() * 1.0) * dpr;
        base = 0.44 + rnd() * 0.46;
      } else {
        r = (1.5 + rnd() * 1.2) * dpr;
        base = 0.74 + rnd() * 0.26;
      }
      return { x: p.x, y: ((p.y % skyH) + skyH) % skyH, r, base, warm };
    };

    for (let i = 0; i < nDust; i++) stars.push(mk('dust'));
    for (let i = 0; i < nNormal; i++) stars.push(mk('normal'));
    for (let i = 0; i < nAnchor; i++) stars.push(mk('anchor'));

    const bright = stars.filter((s) => s.r > 0.9 * dpr);
    for (let i = bright.length - 1; i > 0; i--) {
      const j = (rnd() * (i + 1)) | 0;
      const tmp = bright[i];
      bright[i] = bright[j];
      bright[j] = tmp;
    }
    for (let i = 0; i < Math.min(44, bright.length); i++) {
      bright[i].tw = { amp: 0.2 + rnd() * 0.3, phase: rnd() * 6.28, speed: 0.5 + rnd() * 0.75 };
    }

    const nEm = reduced ? 0 : 28;
    for (let i = 0; i < nEm; i++) {
      const pop = rnd() < 0.22;
      embers.push({
        x: (0.28 + rnd() * 0.44) * W,
        y: H * (0.55 + rnd() * 0.5),
        r: (pop ? 1.4 : 0.8) * (1 + rnd() * 1.4) * dpr,
        vy: (6 + rnd() * 11) * dpr,
        drift: (rnd() - 0.5) * 9 * dpr,
        phase: rnd() * 6.28,
        sp: 0.5 + rnd() * 0.6,
        pop,
      });
    }
  }

  function resize() {
    const nextDpr = Math.min(window.devicePixelRatio || 1, 2);
    const nextW = Math.floor(window.innerWidth * nextDpr);
    const nextH = Math.floor(window.innerHeight * nextDpr);
    // Ignore height-only jitter (mobile URL bar) so ordinary scrolling doesn't reshuffle the field.
    if (nextW === W && nextDpr === dpr && Math.abs(nextH - H) < H * 0.2) return;
    dpr = nextDpr;
    W = nextW;
    H = nextH;
    canvas.width = W;
    canvas.height = H;
    build();
    if (reduced) drawStatic(currentPan());
  }

  // Position a figure across the achievable pan range so every figure reveals as you scroll.
  function figureY(cy: number): number {
    return H * 0.1 + cy * (panRange + H * 0.7);
  }

  function edgeFade(cyN: number): number {
    if (cyN < 0.1) return Math.max(0, cyN / 0.1);
    if (cyN > 0.9) return Math.max(0, (1 - cyN) / 0.1);
    return 1;
  }

  function drawConstellations(t: number, pan: number) {
    for (const con of CONSTELLATIONS) {
      const cyScreen = figureY(con.cy) - pan;
      if (cyScreen < -0.45 * H || cyScreen > 1.45 * H) continue;
      const ef = edgeFade(cyScreen / H);
      if (ef <= 0) continue;
      const s = con.size * W;
      const cx = con.cx * W;
      const nodes = con.nodes;

      let activeEdge = -1;
      let edgePhase = 0;
      if (con.travel && !reduced) {
        const total = con.links.length + 2;
        const pos = (t / 820) % total;
        activeEdge = Math.floor(pos);
        edgePhase = pos - activeEdge;
      }

      for (let l = 0; l < con.links.length; l++) {
        const a = nodes[con.links[l][0]];
        const b = nodes[con.links[l][1]];
        const lit = l === activeEdge ? 0.5 + 0.5 * Math.sin(edgePhase * Math.PI) : 0;
        ctx.strokeStyle = `rgba(240,174,73,${(con.lineBase + lit * 0.32) * ef})`;
        ctx.lineWidth = (0.85 + lit * 0.5) * dpr;
        ctx.lineCap = 'round';
        if (lit > 0.1) {
          ctx.shadowColor = 'rgba(245,180,90,0.9)';
          ctx.shadowBlur = 7 * dpr * lit;
        }
        ctx.beginPath();
        ctx.moveTo(cx + a[0] * s, cyScreen + a[1] * s);
        ctx.lineTo(cx + b[0] * s, cyScreen + b[1] * s);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
      for (let n = 0; n < nodes.length; n++) {
        const nd = nodes[n];
        const mag = nd[2];
        const nodeLit =
          con.travel && !reduced && (n === activeEdge || n === activeEdge + 1) ? 0.32 : 0;
        blitStar(
          true,
          cx + nd[0] * s,
          cyScreen + nd[1] * s,
          (1.15 + mag * 1.75) * dpr,
          (0.42 + mag * 0.5 + nodeLit) * ef
        );
      }
    }

    const pcy = figureY(PLEIADES.cy) - pan;
    if (pcy > -0.2 * H && pcy < 1.2 * H) {
      const ef = edgeFade(pcy / H);
      const s = PLEIADES.size * W;
      const cx = PLEIADES.cx * W;
      for (const p of PLEIADES.points)
        blitStar(true, cx + p[0] * s, pcy + p[1] * s, 1.0 * dpr, 0.55 * ef);
    }
  }

  function spawnShooting() {
    const fromLeft = Math.random() < 0.7;
    const sx = (fromLeft ? 0.05 + Math.random() * 0.3 : 0.65 + Math.random() * 0.3) * W;
    const sy = (0.04 + Math.random() * 0.22) * H;
    const ang = (fromLeft ? 0.28 : 0.72) * Math.PI + (Math.random() - 0.5) * 0.3;
    const speed = (620 + Math.random() * 380) * dpr;
    shooting = {
      x: sx,
      y: sy,
      vx: Math.cos(ang) * speed,
      vy: Math.abs(Math.sin(ang)) * speed,
      life: 0,
      max: 0.95 + Math.random() * 0.5,
      len: (70 + Math.random() * 60) * dpr,
    };
  }
  function drawShooting(dt: number) {
    if (!shooting) return;
    shooting.life += dt;
    shooting.x += shooting.vx * dt;
    shooting.y += shooting.vy * dt;
    const p = shooting.life / shooting.max;
    if (p >= 1) {
      shooting = null;
      return;
    }
    const fade = p < 0.12 ? p / 0.12 : p > 0.78 ? (1 - p) / 0.22 : 1;
    const mag = Math.hypot(shooting.vx, shooting.vy) || 1;
    const tx = shooting.x - (shooting.vx / mag) * shooting.len;
    const ty = shooting.y - (shooting.vy / mag) * shooting.len;
    const g = ctx.createLinearGradient(tx, ty, shooting.x, shooting.y);
    g.addColorStop(0, 'rgba(200,215,255,0)');
    g.addColorStop(0.6, `rgba(224,234,255,${0.18 * fade})`);
    g.addColorStop(0.92, `rgba(245,235,255,${0.5 * fade})`);
    g.addColorStop(1, `rgba(255,224,180,${0.6 * fade})`);
    ctx.strokeStyle = g;
    ctx.lineWidth = 1.6 * dpr;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(shooting.x, shooting.y);
    ctx.stroke();
    ctx.fillStyle = `rgba(255,245,230,${0.85 * fade})`;
    ctx.beginPath();
    ctx.arc(shooting.x, shooting.y, 1.7 * dpr, 0, 6.2832);
    ctx.fill();
  }
  function drawEmbers(t: number, dt: number) {
    for (const e of embers) {
      e.y -= e.vy * dt;
      e.x += Math.sin(t * 0.0006 * e.sp + e.phase) * e.drift * dt;
      if (e.y < H * 0.35) {
        e.y = H * (0.98 + Math.random() * 0.06);
        e.x = (0.28 + Math.random() * 0.44) * W;
      }
      const puls = 0.5 + 0.5 * Math.sin(t * 0.0016 * e.sp + e.phase);
      blitEmber(e.x, e.y, e.r, 0.14 + puls * ((e.pop ? 0.6 : 0.4) - 0.14));
    }
  }

  let lastPubScroll = Number.NaN;
  function setParallaxVars(scrollY: number) {
    if (!parent || scrollY === lastPubScroll) return;
    lastPubScroll = scrollY;
    parent.style.setProperty('--eb-par-wash', `${-scrollY * 0.05}px`);
    parent.style.setProperty('--eb-par-neb', `${-scrollY * 0.09}px`);
    parent.style.setProperty('--eb-par-band', `${-scrollY * 0.07}px`);
    parent.style.setProperty(
      '--eb-hero-glow',
      String(Math.max(0.12, 1 - scrollY / (window.innerHeight * 0.9)))
    );
  }

  function currentPan(): number {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    return Math.min(scrollY * dpr * PAN_FACTOR, panRange);
  }

  function drawStars(t: number, pan: number) {
    const m = 80 * dpr;
    for (const s of stars) {
      const dy = s.y - pan;
      if (dy < -m || dy > H + m) continue;
      const a = s.tw
        ? Math.max(0.04, s.base + Math.sin(t * 0.001 * s.tw.speed + s.tw.phase) * s.tw.amp)
        : s.base;
      blitStar(s.warm, s.x, dy, s.r, a);
    }
  }

  // Static (no twinkle/embers) — used at rest and by the reduced-motion scroll handler.
  function drawStatic(pan: number) {
    ctx.clearRect(0, 0, W, H);
    drawStars(0, pan);
    drawConstellations(0, pan);
    setParallaxVars(window.scrollY || 0);
  }

  function frame(ts: number) {
    const dt = last ? Math.min(0.05, (ts - last) / 1000) : 0.016;
    last = ts;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const pan = Math.min(scrollY * dpr * PAN_FACTOR, panRange);
    setParallaxVars(scrollY);

    ctx.clearRect(0, 0, W, H);
    drawStars(ts, pan);
    drawConstellations(ts, pan);
    drawEmbers(ts, dt);
    if (!shooting && ts >= nextShootAt) {
      spawnShooting();
      const early = ts - startTs < 14000; // more frequent in the first seconds (the hero)
      nextShootAt = ts + (early ? 2400 + Math.random() * 2600 : 6000 + Math.random() * 6000);
    }
    drawShooting(dt);
    raf = requestAnimationFrame(frame);
  }

  const onResize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(resize, 150);
  };
  // Late content (images/fonts) changes page height → re-size the sky so it always spans the page.
  const remeasure = () => {
    window.clearTimeout(roTimer);
    roTimer = window.setTimeout(() => {
      const nextSky = computeSky();
      if (Math.abs(nextSky - skyH) > skyH * 0.1) {
        build(); // page grew/shrank meaningfully — regenerate to fit (happens early, before real scroll)
      }
      if (reduced) drawStatic(currentPan());
    }, 250);
  };
  // Reduced motion: no self-running loop, but still pan the static sky on the user's own scroll.
  const onScrollReduced = () => {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      drawStatic(currentPan());
    });
  };

  const bodyRO = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(remeasure) : null;

  resize();
  window.addEventListener('resize', onResize);
  window.addEventListener('load', remeasure);
  if (bodyRO) bodyRO.observe(document.body);

  if (reduced) {
    window.addEventListener('scroll', onScrollReduced, { passive: true });
    drawStatic(currentPan());
  } else {
    raf = requestAnimationFrame((ts) => {
      last = ts;
      nextShootAt = ts + 1200;
      frame(ts);
    });
  }

  return {
    destroy: () => {
      if (raf) cancelAnimationFrame(raf);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      window.clearTimeout(resizeTimer);
      window.clearTimeout(roTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', remeasure);
      window.removeEventListener('scroll', onScrollReduced);
      if (bodyRO) bodyRO.disconnect();
      if (parent) {
        parent.style.removeProperty('--eb-par-wash');
        parent.style.removeProperty('--eb-par-neb');
        parent.style.removeProperty('--eb-par-band');
        parent.style.removeProperty('--eb-hero-glow');
      }
    },
  };
}
